/**
 * Icône d'un POI dans la liste — EF-RBK-002, EF-RBK-012.
 *
 * Chaque icône reste une commande indépendante ouvrant le détail du POI
 * correspondant, y compris à l'intérieur d'un groupe de proximité.
 */

import type { JSX } from 'preact';
import { categoryFamily, categoryLabel, type CategoryKey } from '../../core/categories';
import type { Poi } from '../../core/model';
import { primaryCategory } from '../../core/model';
import { CategoryIcon, PinRing } from '../icons';

const FAMILY_CLASS: Readonly<Record<string, string>> = {
  water: 'family-water',
  supply: 'family-supply',
  rest: 'family-rest',
  race: 'family-race',
};

export interface PoiIconButtonProps {
  readonly poi: Poi;
  readonly hiddenCategories: ReadonlySet<CategoryKey>;
  readonly onOpen: () => void;
  /** Indicateur non chromatique du POI mis en évidence dans un groupe. */
  readonly emphasized?: boolean;
}

export function PoiIconButton(props: PoiIconButtonProps): JSX.Element | null {
  const category = primaryCategory(props.poi, props.hiddenCategories);
  if (category === null) return null;

  const pinned = props.poi.userState.pinned;
  const family = FAMILY_CLASS[categoryFamily(category)] ?? '';
  const name = props.poi.name ?? categoryLabel(category);
  const icon = <CategoryIcon category={category} pinned={pinned} decorative />;

  return (
    <button
      type="button"
      class={family}
      onClick={props.onOpen}
      aria-label={`${categoryLabel(category)}${pinned ? ', épinglé' : ''} — ${name}. Ouvrir le détail.`}
      style={props.emphasized === true ? 'text-decoration: underline; text-underline-offset: 4px;' : undefined}
    >
      {pinned ? <PinRing>{icon}</PinRing> : icon}
    </button>
  );
}
